'use client';

/**
 * usePaletteChat Hook
 *
 * Issue #261: AI-Powered Data Chat Enhancement - Phase 4 (Session Management)
 *
 * Provides chat functionality for the Command Palette with:
 * - Streaming message support
 * - Server-side session persistence
 * - Local storage fallback
 * - Entity context awareness
 * - Approval request handling
 *
 * @see https://sdk.vercel.ai/docs/ai-sdk-ui/useChat
 */

import type { ConfidenceAssessment, RecommendationSeverity, ToolExecutionStatus } from '@ops-matrix/shared-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Chat chunk types for streaming responses
 */
export type PaletteChatChunkType =
  | 'TEXT'
  | 'THINKING'
  | 'TOOL_USE'
  | 'TOOL_RESULT'
  | 'FINAL_ANSWER'
  | 'UI_COMPONENT'
  | 'APPROVAL_REQUEST'
  | 'SOURCES'
  | 'CONFIDENCE'
  | 'SUGGESTIONS'
  | 'ERROR';

/**
 * Streaming chunk from the server
 */
export interface PaletteChatChunk {
  type: PaletteChatChunkType;
  content?: string;
  delta?: boolean;
  toolId?: string;
  toolName?: string;
  parameters?: Record<string, unknown>;
  result?: unknown;
  component?: {
    id: string;
    type: string;
    props: Record<string, unknown>;
  };
  approvalRequest?: {
    id: string;
    toolName: string;
    message: string;
    parameters: Record<string, unknown>;
    riskLevel: `${RecommendationSeverity}`;
    expiresAt: string;
  };
  sources?: Array<{
    id: string;
    content: string;
    score: number;
    title?: string;
  }>;
  confidence?: {
    score: number;
    level: ConfidenceAssessment['level'];
    factors: Array<{ name: string; score: number }>;
  };
  suggestions?: string[];
  error?: {
    code: string;
    message: string;
  };
}

/**
 * Chat message in the conversation
 */
export interface PaletteChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  uiComponents?: Array<{
    id: string;
    type: string;
    props: Record<string, unknown>;
  }>;
  approvalRequest?: PaletteChatChunk['approvalRequest'];
  sources?: PaletteChatChunk['sources'];
  confidence?: PaletteChatChunk['confidence'];
  suggestions?: string[];
  toolInvocations?: Array<{
    toolId: string;
    toolName: string;
    status: `${ToolExecutionStatus}`;
    result?: unknown;
  }>;
  error?: PaletteChatChunk['error'];
  entityContext?: {
    id: string;
    type: string;
    title: string;
  };
}

/**
 * Entity context for chat
 */
export interface PaletteChatEntityContext {
  entityType: string;
  entityId: string;
  entityLabel?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Server session data
 */
export interface PaletteChatSession {
  conversationId: string;
  messages: PaletteChatMessage[];
  entityContext?: PaletteChatEntityContext;
  timestamp: Date;
}

/**
 * Send function signature for streaming chat
 */
export type PaletteChatSendFn = (
  message: string,
  options?: {
    conversationId?: string;
    entityContext?: PaletteChatEntityContext;
    onChunk?: (chunk: PaletteChatChunk) => void;
    signal?: AbortSignal;
  },
) => AsyncGenerator<PaletteChatChunk, void, unknown>;

/**
 * Hook options
 */
export interface UsePaletteChatOptions {
  /** Agent ID for identifying the assistant */
  agentId?: string;
  /** Function to send messages and receive streaming chunks */
  sendFn?: PaletteChatSendFn;
  /** Storage key for localStorage persistence */
  storageKey?: string;
  /** Session timeout in milliseconds (default: 30 minutes) */
  sessionTimeout?: number;
  /** Whether to persist to server */
  persistToServer?: boolean;
  /** Function to persist session to server */
  persistSession?: (session: PaletteChatSession) => Promise<void>;
  /** Function to restore session from server */
  restoreSession?: (storageKey: string) => Promise<PaletteChatSession | null>;
  /** Initial entity context */
  initialEntityContext?: PaletteChatEntityContext;
  /** Callback when approval is requested */
  onApprovalRequest?: (request: PaletteChatChunk['approvalRequest']) => void;
  /** Maximum messages to keep in memory */
  maxMessages?: number;
}

/**
 * Hook return value
 */
export interface UsePaletteChatReturn {
  /** Current messages in the conversation */
  messages: PaletteChatMessage[];
  /** Whether a message is currently being sent/streamed */
  isLoading: boolean;
  /** Whether a message is currently being streamed (alias for isLoading) */
  isStreaming: boolean;
  /** Current error if any */
  error: Error | null;
  /** Current conversation ID */
  conversationId: string;
  /** Current entity context */
  entityContext: PaletteChatEntityContext | null;
  /** Pending approval request */
  pendingApproval: PaletteChatChunk['approvalRequest'] | null;
  /** Send a message */
  sendMessage: (content: string) => Promise<void>;
  /** Set entity context */
  setEntityContext: (context: PaletteChatEntityContext | null) => void;
  /** Clear chat history */
  clearChat: () => void;
  /** Clear chat messages (alias for clearChat) */
  clearMessages: () => void;
  /** Clear entity context */
  clearContext: () => void;
  /** Abort current streaming */
  abort: () => void;
  /** Respond to approval request */
  respondToApproval: (
    approvalId: string,
    decision: 'approve' | 'reject' | 'edit',
    editedParams?: Record<string, unknown>,
  ) => Promise<void>;
  /** Regenerate last response */
  regenerate: () => Promise<void>;
  /** Add a message manually */
  addMessage: (message: Omit<PaletteChatMessage, 'id' | 'timestamp'>) => void;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

function createMessage(partial: Omit<PaletteChatMessage, 'id' | 'timestamp'>): PaletteChatMessage {
  return {
    ...partial,
    id: generateId(),
    timestamp: new Date(),
  };
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

export function usePaletteChat(options: UsePaletteChatOptions = {}): UsePaletteChatReturn {
  const {
    sendFn,
    storageKey = 'palette-chat',
    sessionTimeout = 30 * 60 * 1000, // 30 minutes
    persistToServer = false,
    persistSession,
    restoreSession,
    initialEntityContext,
    onApprovalRequest,
    maxMessages = 100,
  } = options;

  // State
  const [messages, setMessages] = useState<PaletteChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [conversationId, setConversationId] = useState<string>(generateId());
  const [entityContext, setEntityContext] = useState<PaletteChatEntityContext | null>(initialEntityContext ?? null);
  const [pendingApproval, setPendingApproval] = useState<PaletteChatChunk['approvalRequest'] | null>(null);

  // Refs
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastUserMessageRef = useRef<string>('');

  // ============================================================================
  // PERSISTENCE
  // ============================================================================

  // Save to localStorage
  const saveToStorage = useCallback(
    (msgs: PaletteChatMessage[], convId: string, ctx: PaletteChatEntityContext | null) => {
      try {
        const session: PaletteChatSession = {
          conversationId: convId,
          messages: msgs.slice(-maxMessages),
          entityContext: ctx ?? undefined,
          timestamp: new Date(),
        };
        localStorage.setItem(storageKey, JSON.stringify(session));
      } catch {
        // localStorage might be full or disabled
      }
    },
    [storageKey, maxMessages],
  );

  // Load from localStorage
  const loadFromStorage = useCallback((): PaletteChatSession | null => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return null;

      const session: PaletteChatSession = JSON.parse(stored);

      // Check if session is expired
      if (Date.now() - new Date(session.timestamp).getTime() > sessionTimeout) {
        localStorage.removeItem(storageKey);
        return null;
      }

      return session;
    } catch {
      return null;
    }
  }, [storageKey, sessionTimeout]);

  // Sync to server
  const syncToServer = useCallback(
    async (msgs: PaletteChatMessage[], convId: string, ctx: PaletteChatEntityContext | null) => {
      if (!persistToServer || !persistSession) return;

      try {
        await persistSession({
          conversationId: convId,
          messages: msgs.slice(-10), // Only sync last 10 messages
          entityContext: ctx ?? undefined,
          timestamp: new Date(),
        });
      } catch (err) {
        console.warn('Failed to sync session to server:', err);
      }
    },
    [persistToServer, persistSession],
  );

  // Restore session on mount
  useEffect(() => {
    const restore = async () => {
      // Try server first
      if (persistToServer && restoreSession) {
        try {
          const serverSession = await restoreSession(storageKey);
          if (serverSession && Date.now() - new Date(serverSession.timestamp).getTime() < sessionTimeout) {
            setMessages(serverSession.messages);
            setConversationId(serverSession.conversationId);
            if (serverSession.entityContext) {
              setEntityContext(serverSession.entityContext);
            }
            return;
          }
        } catch {
          // Fall through to localStorage
        }
      }

      // Fallback to localStorage
      const localSession = loadFromStorage();
      if (localSession) {
        setMessages(localSession.messages);
        setConversationId(localSession.conversationId);
        if (localSession.entityContext) {
          setEntityContext(localSession.entityContext);
        }
      }
    };

    restore();
  }, [persistToServer, restoreSession, loadFromStorage, sessionTimeout, storageKey]);

  // Save on message change
  useEffect(() => {
    if (messages.length > 0) {
      saveToStorage(messages, conversationId, entityContext);
      syncToServer(messages, conversationId, entityContext);
    }
  }, [messages, conversationId, entityContext, saveToStorage, syncToServer]);

  // ============================================================================
  // MESSAGE HANDLING
  // ============================================================================

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || !sendFn) {
        setError(new Error('Cannot send empty message or sendFn not provided'));
        return;
      }

      // Abort any existing stream
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // Store for regeneration
      lastUserMessageRef.current = content;

      // Add user message
      const userMessage = createMessage({ role: 'user', content });
      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);
      setPendingApproval(null);

      // Create placeholder for assistant message
      const assistantMessageId = generateId();
      const assistantMessage: PaletteChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
        uiComponents: [],
        toolInvocations: [],
      };
      setMessages((prev) => [...prev, assistantMessage]);

      try {
        const stream = sendFn(content, {
          conversationId,
          entityContext: entityContext ?? undefined,
          signal: abortControllerRef.current.signal,
        });

        for await (const chunk of stream) {
          setMessages((prev) => {
            const updated = [...prev];
            const idx = updated.findIndex((m) => m.id === assistantMessageId);
            if (idx === -1) return prev;

            const msg = { ...updated[idx] };

            switch (chunk.type) {
              case 'TEXT':
                if (chunk.delta) {
                  msg.content += chunk.content ?? '';
                } else {
                  msg.content = chunk.content ?? '';
                }
                break;

              case 'THINKING':
                // Could display thinking indicator
                break;

              case 'TOOL_USE':
                msg.toolInvocations = [
                  ...(msg.toolInvocations ?? []),
                  {
                    toolId: chunk.toolId ?? '',
                    toolName: chunk.toolName ?? '',
                    status: 'executing',
                  },
                ];
                break;

              case 'TOOL_RESULT':
                msg.toolInvocations = msg.toolInvocations?.map((t) =>
                  t.toolId === chunk.toolId ? { ...t, status: 'completed' as const, result: chunk.result } : t,
                );
                break;

              case 'UI_COMPONENT':
                if (chunk.component) {
                  msg.uiComponents = [...(msg.uiComponents ?? []), chunk.component];
                }
                break;

              case 'APPROVAL_REQUEST':
                if (chunk.approvalRequest) {
                  msg.approvalRequest = chunk.approvalRequest;
                  setPendingApproval(chunk.approvalRequest);
                  onApprovalRequest?.(chunk.approvalRequest);
                }
                break;

              case 'SOURCES':
                msg.sources = chunk.sources;
                break;

              case 'CONFIDENCE':
                msg.confidence = chunk.confidence;
                break;

              case 'SUGGESTIONS':
                msg.suggestions = chunk.suggestions;
                break;

              case 'FINAL_ANSWER':
                msg.content = chunk.content ?? msg.content;
                msg.isStreaming = false;
                break;

              case 'ERROR':
                msg.error = chunk.error;
                msg.isStreaming = false;
                break;
            }

            updated[idx] = msg;
            return updated;
          });
        }

        // Mark streaming complete
        setMessages((prev) => prev.map((m) => (m.id === assistantMessageId ? { ...m, isStreaming: false } : m)));
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // User aborted - mark message as incomplete
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessageId ? { ...m, isStreaming: false, content: m.content || '[Aborted]' } : m,
            ),
          );
        } else {
          setError(err instanceof Error ? err : new Error('Unknown error'));
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessageId
                ? {
                    ...m,
                    isStreaming: false,
                    error: { code: 'SEND_ERROR', message: String(err) },
                  }
                : m,
            ),
          );
        }
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    },
    [sendFn, conversationId, entityContext, onApprovalRequest],
  );

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);
  }, []);

  const clearChat = useCallback(() => {
    abort();
    setMessages([]);
    setConversationId(generateId());
    setPendingApproval(null);
    setError(null);
    localStorage.removeItem(storageKey);
  }, [abort, storageKey]);

  const regenerate = useCallback(async () => {
    if (!lastUserMessageRef.current) return;

    // Remove last assistant message
    setMessages((prev) => {
      // Find last user message index (compatible with ES2022, no findLastIndex)
      let lastUserIdx = -1;
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].role === 'user') {
          lastUserIdx = i;
          break;
        }
      }
      if (lastUserIdx === -1) return prev;
      return prev.slice(0, lastUserIdx + 1).filter((m) => m.role !== 'assistant' || prev.indexOf(m) < lastUserIdx);
    });

    // Resend
    await sendMessage(lastUserMessageRef.current);
  }, [sendMessage]);

  const respondToApproval = useCallback(
    async (_approvalId: string, decision: 'approve' | 'reject' | 'edit', _editedParams?: Record<string, unknown>) => {
      // This would typically call an API endpoint
      // For now, just update local state
      setPendingApproval(null);

      // Add system message about the decision
      const decisionMessage = createMessage({
        role: 'system',
        content:
          decision === 'approve'
            ? 'Action approved and executed.'
            : decision === 'reject'
              ? 'Action rejected.'
              : 'Action approved with modifications.',
      });
      setMessages((prev) => [...prev, decisionMessage]);

      // In a real implementation, this would call:
      // await api.respondToApproval({ _approvalId, decision, _editedParams });
    },
    [],
  );

  const addMessage = useCallback((partial: Omit<PaletteChatMessage, 'id' | 'timestamp'>) => {
    const message = createMessage(partial);
    setMessages((prev) => [...prev, message]);
  }, []);

  // ============================================================================
  // MEMOIZED RETURN
  // ============================================================================

  // Alias functions for backward compatibility
  const clearMessages = clearChat;
  const clearContext = useCallback(() => setEntityContext(null), [setEntityContext]);

  return useMemo(
    () => ({
      messages,
      isLoading,
      isStreaming: isLoading, // Alias for backward compatibility
      error,
      conversationId,
      entityContext,
      pendingApproval,
      sendMessage,
      setEntityContext,
      clearChat,
      clearMessages, // Alias for clearChat
      clearContext, // Clear entity context
      abort,
      respondToApproval,
      regenerate,
      addMessage,
    }),
    [
      messages,
      isLoading,
      error,
      conversationId,
      entityContext,
      pendingApproval,
      sendMessage,
      clearChat,
      clearMessages,
      clearContext,
      abort,
      respondToApproval,
      regenerate,
      addMessage,
    ],
  );
}

export default usePaletteChat;
